/**
 * FinanceDashboardPage
 * 
 * Página principal do dashboard financeiro.
 * Exibe visão consolidada da saúde financeira do negócio.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Wallet, ArrowUpRight, ArrowDownRight, Activity,
  Receipt, TrendingUp, Landmark, FileText,
  Calendar as CalendarIcon, MoreVertical, BarChart3, CreditCard, AlertTriangle,
  Layers, LineChart, ClipboardCheck, RotateCcw, PieChart, Scale
} from 'lucide-react'
import { CalendarRangePicker } from '../../../../components/ERP/Calendar/Calendar'
import { formatDateOnly, toDateInputValue } from '../../../../utils/dateUtils'
import { financeDashboardService } from '../../../../services/financeDashboardService'
import KPICard from '../../../../components/ERP/FinanceDashboard/KPICard/KPICard'
import CostCenterChart from '../../../../components/ERP/FinanceDashboard/CostCenterChart/CostCenterChart'
import ProjectionCard from '../../../../components/ERP/FinanceDashboard/ProjectionCard/ProjectionCard'
import DonutChart from '../../../../components/ERP/FinanceDashboard/DonutChart/DonutChart'
import CashFlow48hSummary from '../../../../components/ERP/FinanceDashboard/CashFlow48hSummary/CashFlow48hSummary'
import BalanceProjectionChart from '../../../../components/ERP/FinanceDashboard/BalanceProjectionChart/BalanceProjectionChart'
import MonthlyFinancialBarChart from '../../../../components/ERP/FinanceDashboard/MonthlyFinancialBarChart/MonthlyFinancialBarChart'
import { usePermissions, MODULES, ACTIONS } from '../../../../hooks/usePermissions'
import './FinanceDashboardPage.css'

const PLACEHOLDER_KPIS = [
  { label: 'Saldo em Caixa', value: '—', change: '—', up: true, icon: Wallet, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  { label: 'A Receber', value: '—', change: '—', up: true, icon: ArrowUpRight, color: 'text-blue-500', bg: 'bg-blue-50' },
  { label: 'A Pagar', value: '—', change: '—', up: false, icon: ArrowDownRight, color: 'text-rose-500', bg: 'bg-rose-50' },
  { label: 'A Receber Atrasadas', value: '—', change: '—', up: false, icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' },
  { label: 'A Pagar Atrasadas', value: '—', change: '—', up: false, icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50' },
  { label: 'Margem Líquida', value: '—', change: '—', up: true, icon: Activity, color: 'text-primary-500', bg: 'bg-primary-50' },
]

function getDefaultDateRange() {
  const today = new Date()
  const end = new Date(today)
  end.setDate(end.getDate() + 7)
  return {
    from: toDateInputValue(today),
    to: toDateInputValue(end)
  }
}

function buildPeriodLabel(from, to) {
  if (!from || !to) return 'Selecionar período'
  return `${formatDateOnly(from)} — ${formatDateOnly(to)}`
}

function FinanceDashboardPage() {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [initialLoading, setInitialLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const [analysisData, setAnalysisData] = useState(null)
  const [error, setError] = useState(null)
  const [dateRange, setDateRange] = useState(getDefaultDateRange)
  const [isPeriodOpen, setIsPeriodOpen] = useState(false)
  const periodBtnRef = useRef(null)
  const [projectionDays, setProjectionDays] = useState(120)

  const period = useMemo(() => {
    const [year, month] = (dateRange.from || '').split('-').map(Number)
    return { month, year, dateFrom: dateRange.from, dateTo: dateRange.to }
  }, [dateRange.from, dateRange.to])

  const pendingLoadsRef = useRef(0)
  const hasDisplayedDataRef = useRef(false)

  const beginFetch = useCallback(() => {
    pendingLoadsRef.current += 1
    if (hasDisplayedDataRef.current) {
      setRefreshing(true)
    } else {
      setInitialLoading(true)
    }
  }, [])

  const endFetch = useCallback(() => {
    pendingLoadsRef.current = Math.max(0, pendingLoadsRef.current - 1)
    if (pendingLoadsRef.current === 0) {
      setInitialLoading(false)
      setRefreshing(false)
    }
  }, [])

  const periodLabel = useMemo(
    () => buildPeriodLabel(dateRange.from, dateRange.to),
    [dateRange.from, dateRange.to]
  )

  const loadDashboardData = useCallback(async () => {
    beginFetch()
    setError(null)
    try {
      const response = await financeDashboardService.getDashboardData({
        month: period.month,
        year: period.year,
        dateFrom: period.dateFrom,
        dateTo: period.dateTo
      })
      if (response.success) {
        setDashboardData(response.data)
        hasDisplayedDataRef.current = true
      } else {
        setError('Erro ao carregar dados do dashboard')
      }
    } catch (err) {
      console.error('Erro ao carregar dados do dashboard:', err)
      setError('Erro ao carregar dados. Tente novamente.')
    } finally {
      endFetch()
    }
  }, [period.month, period.year, period.dateFrom, period.dateTo, beginFetch, endFetch])

  const loadAnalysisData = useCallback(async () => {
    beginFetch()
    try {
      const response = await financeDashboardService.getAnalysisData({
        month: period.month,
        year: period.year,
        dateFrom: period.dateFrom,
        dateTo: period.dateTo,
        days: projectionDays
      })
      if (response.success) {
        setAnalysisData(response.data)
        hasDisplayedDataRef.current = true
      }
    } catch (err) {
      console.error('Erro ao carregar dados de análise:', err)
    } finally {
      endFetch()
    }
  }, [period.month, period.year, period.dateFrom, period.dateTo, projectionDays, beginFetch, endFetch])

  const handleRefresh = useCallback(() => {
    loadDashboardData()
    loadAnalysisData()
  }, [loadDashboardData, loadAnalysisData])

  useEffect(() => {
    loadDashboardData()
  }, [loadDashboardData])

  useEffect(() => {
    loadAnalysisData()
  }, [loadAnalysisData])

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatChange = (value, isPercentage = false) => {
    if (value === 0) return '0'
    const sign = value > 0 ? '+' : ''
    if (isPercentage) {
      return `${sign}${value.toFixed(1)}%`
    }
    if (Math.abs(value) >= 1000) {
      return `${sign}R$ ${(value / 1000).toFixed(1)}k`
    }
    return `${sign}R$ ${value.toFixed(0)}`
  }

  // Preparar dados dos KPIs
  const liquidityStats = dashboardData?.liquidityStats
  const titlesPeriodLabel = buildPeriodLabel(
    liquidityStats?.receivable7Days?.dateFrom || dateRange.from,
    liquidityStats?.receivable7Days?.dateTo || dateRange.to
  )

  const handlePeriodRangeChange = (from, to) => {
    if (!from || !to) return
    setDateRange({ from, to })
  }

  const handleClearPeriodFilter = () => {
    setDateRange(getDefaultDateRange())
  }

  const kpiData = liquidityStats ? [
    {
      label: 'Saldo em Caixa',
      value: formatCurrency(liquidityStats.cashBalance?.value),
      change: formatChange(liquidityStats.cashBalance?.change || 0),
      up: liquidityStats.cashBalance?.changeType === 'increase',
      icon: Wallet,
      color: 'text-emerald-500',
      bg: 'bg-emerald-50'
    },
    {
      label: 'A Receber',
      value: formatCurrency(liquidityStats.receivable7Days?.amount),
      change: `${liquidityStats.receivable7Days?.count || 0} títulos`,
      up: true,
      icon: ArrowUpRight,
      color: 'text-blue-500',
      bg: 'bg-blue-50',
      details: {
        title: `Contas a receber (${titlesPeriodLabel})`,
        actionLabel: 'Abrir contas a receber',
        actionPath: '/erp/financeiro/contas-receber',
        items: liquidityStats.receivable7Days?.items || [],
        kind: 'receivable',
        preset: 'dateRange',
        dueDateFrom: liquidityStats.receivable7Days?.dateFrom || dateRange.from,
        dueDateTo: liquidityStats.receivable7Days?.dateTo || dateRange.to
      }
    },
    {
      label: 'A Pagar',
      value: formatCurrency(liquidityStats.payable7Days?.amount),
      change: `${liquidityStats.payable7Days?.count || 0} títulos`,
      up: false,
      icon: ArrowDownRight,
      color: 'text-rose-500',
      bg: 'bg-rose-50',
      details: {
        title: `Contas a pagar (${titlesPeriodLabel})`,
        actionLabel: 'Abrir contas a pagar',
        actionPath: '/erp/financeiro/contas-pagar',
        cardActionPath: '/erp/financeiro/analise-contas-pagar',
        cardActionPreset: 'dateRange',
        items: liquidityStats.payable7Days?.items || [],
        kind: 'payable',
        preset: 'dateRange',
        dueDateFrom: liquidityStats.payable7Days?.dateFrom || dateRange.from,
        dueDateTo: liquidityStats.payable7Days?.dateTo || dateRange.to
      }
    },
    {
      label: 'A Receber Atrasadas',
      value: formatCurrency(liquidityStats.receivableOverdue?.amount),
      change: `${liquidityStats.receivableOverdue?.count || 0} títulos`,
      up: false,
      icon: AlertTriangle,
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      details: {
        title: 'Contas a receber atrasadas',
        actionLabel: 'Abrir contas a receber',
        actionPath: '/erp/financeiro/contas-receber',
        items: liquidityStats.receivableOverdue?.items || [],
        kind: 'receivable',
        preset: 'overdue'
      }
    },
    {
      label: 'A Pagar Atrasadas',
      value: formatCurrency(liquidityStats.payableOverdue?.amount),
      change: `${liquidityStats.payableOverdue?.count || 0} títulos`,
      up: false,
      icon: AlertTriangle,
      color: 'text-orange-500',
      bg: 'bg-orange-50',
      details: {
        title: 'Contas a pagar atrasadas',
        actionLabel: 'Abrir contas a pagar',
        actionPath: '/erp/financeiro/contas-pagar',
        cardActionPath: '/erp/financeiro/analise-contas-pagar',
        cardActionPreset: 'overdue',
        items: liquidityStats.payableOverdue?.items || [],
        kind: 'payable',
        preset: 'overdue'
      }
    },
    {
      label: 'Margem Líquida',
      value: `${(liquidityStats.netMargin?.percentage || 0).toFixed(1)}%`,
      change: formatChange(liquidityStats.netMargin?.change || 0, true),
      up: liquidityStats.netMargin?.changeType === 'increase',
      icon: Activity,
      color: 'text-primary-500',
      bg: 'bg-primary-50'
    }
  ] : []

  const displayKpis = kpiData.length > 0 ? kpiData : PLACEHOLDER_KPIS
  const isBusy = initialLoading || refreshing

  const shortcuts = useMemo(() => {
    const items = [
      { label: 'Contas a Pagar', icon: Receipt, path: '/erp/financeiro/contas-pagar', color: 'bg-rose-600', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_PAYABLES },
      { label: 'Contas a Receber', icon: TrendingUp, path: '/erp/financeiro/contas-receber', color: 'bg-emerald-600', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_RECEIVABLES_VIEW },
      { label: 'Boletos', icon: CreditCard, path: '/erp/financeiro/boletos', color: 'bg-indigo-600', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_RECEIVABLES_VIEW },
      { label: 'Dashboard Boletos', icon: BarChart3, path: '/erp/financeiro/boletos/dashboard', color: 'bg-purple-600', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_RECEIVABLES_VIEW },
      { label: 'Extrato de Caixa', icon: Landmark, path: '/erp/financeiro/extrato-caixa', color: 'bg-slate-900', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CASH_FLOW },
      { label: 'Relatório DRE', icon: FileText, path: '/erp/financeiro/dre', color: 'bg-blue-600', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_DRE },
      { label: 'DFC', icon: Layers, path: '/erp/financeiro/dfc', color: 'bg-cyan-600', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_DFC },
      { label: 'Balanço Contábil', icon: Scale, path: '/erp/financeiro/balanco', color: 'bg-teal-700', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_BALANCE_SHEET },
      { label: 'Análise do Fluxo', icon: LineChart, path: '/erp/financeiro/analise-fluxo-caixa', color: 'bg-amber-600', module: MODULES.FINANCIAL, action: ACTIONS.FINANCIAL_CASH_FLOW },
      { label: 'Aprovação Financeira', icon: ClipboardCheck, path: '/erp/purchases/finance', color: 'bg-orange-600', module: MODULES.PURCHASES, action: ACTIONS.VIEW },
    ]
    return items.filter((item) => hasPermission(item.module, item.action))
  }, [hasPermission])

  if (initialLoading && !dashboardData) {
    return (
      <div className="finance-dashboard-loading">
        <p>Carregando dados do dashboard...</p>
      </div>
    )
  }

  if (error && !dashboardData) {
    return (
      <div className="finance-dashboard-error">
        <p>{error}</p>
        <button type="button" onClick={handleRefresh}>Tentar novamente</button>
      </div>
    )
  }

  return (
    <div className={`finance-dashboard${refreshing ? ' is-refreshing' : ''}`}>
      {/* Header & Quick Filter */}
      <div className="finance-dashboard-header">
        <div>
          <h1 className="finance-dashboard-title">
            Visão <span className="finance-dashboard-title-highlight">Financeira</span>
            {refreshing && (
              <RotateCcw
                size={20}
                className="finance-dashboard-refresh-icon"
                aria-hidden
              />
            )}
          </h1>
          <p className="finance-dashboard-subtitle">
            Controle de Liquidez e Projeção de Fluxo Medik
          </p>
        </div>
        <div className="finance-dashboard-header-actions">
          <button
            type="button"
            onClick={handleRefresh}
            className="finance-dashboard-reload-btn"
            disabled={isBusy}
            aria-label="Atualizar dados do dashboard"
            title="Atualizar dados"
          >
            <RotateCcw
              size={18}
              className={refreshing ? 'finance-dashboard-reload-icon is-spinning' : 'finance-dashboard-reload-icon'}
            />
          </button>
          <button
            ref={periodBtnRef}
            type="button"
            onClick={() => setIsPeriodOpen((prev) => !prev)}
            className="finance-dashboard-period-btn finance-dashboard-period-btn--interactive"
            title="Filtrar período do dashboard"
          >
            <CalendarIcon size={14} className="finance-dashboard-period-icon" />
            <span>{periodLabel}</span>
          </button>
          <CalendarRangePicker
            open={isPeriodOpen}
            onOpenChange={setIsPeriodOpen}
            anchorRef={periodBtnRef}
            startValue={dateRange.from}
            endValue={dateRange.to}
            onRangeChange={handlePeriodRangeChange}
            onClear={handleClearPeriodFilter}
            title="Período do dashboard"
            subtitle="Vencimentos e indicadores"
          />
        </div>
      </div>

      {error && (
        <div className="finance-dashboard-error-banner" role="alert">
          <p>{error}</p>
          <button type="button" onClick={handleRefresh}>Tentar novamente</button>
        </div>
      )}

      {/* Atalhos Rápidos */}
      <div className="finance-dashboard-shortcuts-section">
        <h3 className="finance-dashboard-section-title">Acesso Rápido</h3>
        <div className="finance-dashboard-shortcuts-grid">
          {shortcuts.map((shortcut) => (
            <button
              key={shortcut.path}
              onClick={() => navigate(shortcut.path)}
              className="finance-dashboard-shortcut-btn"
            >
              <div className={`finance-dashboard-shortcut-icon ${shortcut.color}`}>
                <shortcut.icon size={22} />
              </div>
              <span className="finance-dashboard-shortcut-label">{shortcut.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="finance-dashboard-dynamic">
      {/* KPI Cards */}
      <div className="finance-dashboard-kpi-grid">
        {displayKpis.map((kpi, i) => (
          <KPICard
            key={i}
            label={kpi.label}
            value={kpi.value}
            change={kpi.change}
            up={kpi.up}
            icon={kpi.icon}
            color={kpi.color}
            bg={kpi.bg}
            accountBalances={kpi.label === 'Saldo em Caixa' ? analysisData?.accountBalances : null}
            details={kpi.details || null}
          />
        ))}
      </div>

      {(analysisData?.projection48h || analysisData?.opexCategories?.length > 0) && (
        <div className="finance-dashboard-48h-row">
          {analysisData?.projection48h && (
            <CashFlow48hSummary
              variant="light"
              data={analysisData.projection48h}
              showCurrentBalance
              actionLabel="Abrir extrato de caixa"
            />
          )}
          {analysisData?.opexCategories?.length > 0 && (
            <div className="cash-flow-analysis-donut-chart-card finance-dashboard-opex-donut">
              <h3 className="cash-flow-analysis-donut-chart-title">
                <PieChart size={18} /> Distribuição de OPEX (Categorias)
              </h3>
              <DonutChart
                data={analysisData.opexCategories}
                legendMode="floating"
                onItemClick={(item) => {
                  navigate('/erp/financeiro/analise-fluxo-caixa', {
                    state: { initialSearch: item.name }
                  })
                }}
              />
            </div>
          )}
        </div>
      )}

      <BalanceProjectionChart
        balanceProjection={analysisData?.balanceProjection}
        projectionDays={projectionDays}
        onProjectionDaysChange={setProjectionDays}
      />

      {/* Centro de Custos e Projeção */}
      <div className="finance-dashboard-bottom-grid">
        {/* Centro de Custos */}
        <div className="finance-dashboard-cost-centers-section">
          <div className="finance-dashboard-cost-centers-card">
            <div className="finance-dashboard-cost-centers-header">
              <h3 className="finance-dashboard-cost-centers-title">Centro de Custos</h3>
              <button className="finance-dashboard-cost-centers-menu">
                <MoreVertical size={18} />
              </button>
            </div>
            <div className="finance-dashboard-cost-centers-content">
              <CostCenterChart
                data={dashboardData?.costCenters || []}
                onItemClick={(item) => {
                  navigate('/erp/financeiro/analise-fluxo-caixa', { 
                    state: { initialSearch: item.name } 
                  })
                }}
              />
            </div>
            <div className="finance-dashboard-cost-centers-footer">
              <button
                onClick={() => navigate('/erp/financeiro/analise-fluxo-caixa')}
                className="finance-dashboard-cost-centers-btn"
              >
                <BarChart3 size={18} /> Detalhar Fluxo de Caixa
              </button>
            </div>
          </div>
        </div>

        {/* Projeção de Fechamento */}
        <div className="finance-dashboard-projection-section">
          <ProjectionCard
            data={dashboardData?.monthEndProjection}
            onActionClick={() => navigate('/erp/financeiro/contas-receber')}
          />
        </div>
      </div>

      <MonthlyFinancialBarChart data={dashboardData?.monthlyFinancialYear} />
      </div>
    </div>
  )
}

export default FinanceDashboardPage
